import { useParams, Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { AccessTime, Person, Category } from '@mui/icons-material';
import { blogPosts } from '../../data/blogData';
import ReactMarkdown from 'react-markdown';

export default function SingleBlog() {
  const { slug } = useParams();
  const blog = blogPosts.find(post => post.slug === slug);
  const relatedBlogs = blogPosts
    .filter(post => post.slug !== slug && post.category === blog?.category)
    .slice(0, 3);

  if (!blog) {
    return (
      <Container maxWidth="lg" sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>Blog Post Not Found</Typography>
        <Typography>The requested blog post could not be found.</Typography>
        <Box sx={{ mt: 2 }}>
          <Link to="/blog" style={{ textDecoration: 'none' }}>
            <Typography color="primary">← Back to All Blogs</Typography>
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Grid container spacing={3}>
          {/* Main blog content */}
          <Grid item md={8}>
            <Typography variant="h3" gutterBottom>
              {blog.title}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: 1 }} />
                <Typography>{blog.author}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTime sx={{ mr: 1 }} />
                <Typography>{blog.readTime} min read</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Category sx={{ mr: 1 }} />
                <Typography>{blog.category}</Typography>
              </Box>
            </Box>

            <Box 
              component="img"
              src={blog.image}
              sx={{ 
                width: '100%',
                height: 400,
                objectFit: 'cover',
                mb: 3,
                borderRadius: 1
              }}
            />

            <Box sx={{ mb: 3 }}>
              {blog.tags.map(tag => (
                <Chip key={tag} label={tag} sx={{ mr: 1 }} />
              ))}
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ '& > *': { mb: 2 } }}>
                <ReactMarkdown components={{
                  p: ({children}) => <Typography variant="body1">{children}</Typography>,
                  h1: ({children}) => <Typography variant="h4" sx={{mt: 3, mb: 2}}>{children}</Typography>,
                  h2: ({children}) => <Typography variant="h5" sx={{mt: 3, mb: 2}}>{children}</Typography>,
                  h3: ({children}) => <Typography variant="h6" sx={{mt: 3, mb: 2}}>{children}</Typography>,
                  ul: ({children}) => <Box component="ul" sx={{ml: 3, mb: 2}}>{children}</Box>,
                  ol: ({children}) => <Box component="ol" sx={{ml: 3, mb: 2}}>{children}</Box>,
                  li: ({children}) => <Box component="li">{children}</Box>
                }}>
                  {blog.content}
                </ReactMarkdown>
              </Box>
            </Box>

            {/* Ad space within content */}
            <Box sx={{ 
              my: 4,
              p: 2,
              minHeight: 100,
              border: '1px dashed grey',
              textAlign: 'center'
            }}>
              In-Article Ad Space
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar sx={{ width: 60, height: 60, mr: 2 }}>{blog.author[0]}</Avatar>
              <Box>
                <Typography variant="h6">{blog.author}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Professional content writer and time management expert
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item md={4}>
            {/* Ad space */}
            <Box sx={{ 
              mb: 4,
              p: 2,
              minHeight: 250,
              border: '1px dashed grey',
              textAlign: 'center'
            }}>
              Sidebar Ad Space
            </Box>

            {/* Related posts */}
            <Typography variant="h6" gutterBottom>
              Related Posts
            </Typography>
            {relatedBlogs.map(blog => (
              <Card key={blog.id} component={Link} to={`/blog/${blog.slug}`} sx={{ 
                mb: 2,
                textDecoration: 'none'
              }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={blog.image}
                  alt={blog.title}
                />
                <CardContent>
                  <Typography variant="subtitle1">
                    {blog.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <AccessTime sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="caption">{blog.readTime} min read</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}